
import { useParams, useNavigate } from "react-router-dom"
import {
  VStack,
  HStack,
  Text,
  Card,
  Button,
  SimpleGrid,
  Skeleton,
  Badge,
} from "@chakra-ui/react"
import { FaArrowLeft } from "react-icons/fa6"
import { useAlert } from "../hooks/useAlert"
import AlertStatusBadge, { AlertStatusType } from "@hydrologiq/shared/components/AlertStatusBadge"
import AlertSeverityBadge, { AlertSeverityType } from "@hydrologiq/shared/components/AlertSeverityBadge"
import AlertTimeline from "../components/AlertTimeline"
import AlertTriageActions from "../components/AlertTriageActions"
import { toUserTZ } from "@hydrologiq/common/helpers/date"
import { DateTime } from "timezonecomplete"
import { useCopyValues } from "@hydrologiq/shared/providers/helpers/useCopy"
import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"

function formatAlertDate(dateTime: DateTime): string {
  return dateTime.format("MMM dd, yyyy HH:mm")
}

function AlertDetail() {
  const { alertId } = useParams<{ alertId: string }>()
  const navigate = useNavigate()

  const {
    alert,
    loading,
    error,
    refetch,
    updateStatus,
    addNote,
    archive,
    updatingStatus,
    addingNote,
    archiving,
  } = useAlert(alertId!)

  const copy = useCopyValues({
    "headings.alert": "Alert",
    "headings.status": "Status",
    "headings.severity": "Severity",
    "headings.type": "Type",
    "headings.source": "Source",
    "headings.installation": "Installation",
    "headings.message": "Message",
    "headings.fired_at": "Fired At",
    "headings.resolved_at": "Resolved At",
    "headings.assets": "Assets",
    "headings.events": "Timeline",
    "actions.back_to_alerts": "Back to Alerts",
  })

  if (error && !loading) {
    return (
      <VStack gap={4}>
        <ErrorAlert title="Failed to Load Alert" message={error.message} onClose={() => { }} />
        <Button variant="outline" onClick={refetch}>
          Try Again
        </Button>
      </VStack>
    )
  }

  return (
    <VStack gap={6} align="stretch">
      {/* Header */}
      <HStack justify="space-between" align="center">
        <HStack gap={3}>
          <Button
            variant="hydrologiq_secondary"
            leftIcon={<FaArrowLeft />}
            onClick={() => navigate("/alerts")}
          >
            {copy["actions.back_to_alerts"]}
          </Button>
          {loading ? (
            <Skeleton height="32px" width="200px" />
          ) : (
            <Text fontSize="2xl" fontWeight="bold">
              {alert?.template_name || alert?.name}
            </Text>
          )}
        </HStack>

        {alert && !loading && (
          <AlertTriageActions
            alertId={alert.id}
            currentStatus={alert.status as AlertStatusType}
            onUpdateStatus={updateStatus}
            onAddNote={addNote}
            onArchive={archive}
            updatingStatus={updatingStatus}
            addingNote={addingNote}
            archiving={archiving}
          />
        )}
      </HStack>

      <SimpleGrid columns={{ base: 1, lg: 2 }} gap={6}>
        {/* Alert Information */}
        <VStack gap={4} align="stretch">
          <Card.Root>
            <Card.Header>
              <Text fontSize="lg" fontWeight="semibold">
                Alert Information
              </Text>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <VStack gap={3} align="stretch">
                  {[...Array(8)].map((_, index) => (
                    <HStack key={index} justify="space-between">
                      <Skeleton height="20px" width="100px" />
                      <Skeleton height="20px" width="150px" />
                    </HStack>
                  ))}
                </VStack>
              ) : alert ? (
                <VStack gap={3} align="stretch">
                  <HStack justify="space-between">
                    <Text fontWeight="medium">{copy["headings.severity"]}:</Text>
                    <AlertSeverityBadge severity={alert.severity as AlertSeverityType} />
                  </HStack>

                  <HStack justify="space-between">
                    <Text fontWeight="medium">{copy["headings.status"]}:</Text>
                    <AlertStatusBadge status={alert.status as AlertStatusType} />
                  </HStack>

                  <HStack justify="space-between">
                    <Text fontWeight="medium">{copy["headings.type"]}:</Text>
                    <Text>{alert.type}</Text>
                  </HStack>

                  <HStack justify="space-between">
                    <Text fontWeight="medium">{copy["headings.source"]}:</Text>
                    <Text>{alert.source}</Text>
                  </HStack>

                  <HStack justify="space-between">
                    <Text fontWeight="medium">{copy["headings.installation"]}:</Text>
                    <Text>{alert.installation_name || "Unknown"}</Text>
                  </HStack>

                  <HStack justify="space-between" align="start">
                    <Text fontWeight="medium">{copy["headings.message"]}:</Text>
                    <Text textAlign="right" maxW="60%">
                      {alert.message}
                    </Text>
                  </HStack>

                  <HStack justify="space-between">
                    <Text fontWeight="medium">{copy["headings.fired_at"]}:</Text>
                    <Text>{formatAlertDate(toUserTZ(alert.fired_at))}</Text>
                  </HStack>

                  {alert.resolved_at && (
                    <HStack justify="space-between">
                      <Text fontWeight="medium">{copy["headings.resolved_at"]}:</Text>
                      <Text>{formatAlertDate(toUserTZ(alert.resolved_at))}</Text>
                    </HStack>
                  )}
                </VStack>
              ) : null}
            </Card.Body>
          </Card.Root>

          {/* Associated Assets */}
          {alert && alert.assets && alert.assets.length > 0 && (
            <Card.Root>
              <Card.Header>
                <Text fontSize="lg" fontWeight="semibold">
                  {copy["headings.assets"]} ({alert.assets.length})
                </Text>
              </Card.Header>
              <Card.Body>
                <VStack gap={2} align="stretch">
                  {alert.assets.map((asset) => (
                    <HStack key={asset.id} justify="space-between" p={2} bg="bg.subtle" borderRadius="md">
                      <VStack align="start" gap={0}>
                        <Text fontWeight="medium">{asset.name || asset.id}</Text>
                        {asset.kind && <Text fontSize="sm" color="fg.muted">{asset.kind}</Text>}
                      </VStack>
                      {asset.status && (
                        <Badge variant="subtle" colorPalette="blue">
                          {asset.status}
                        </Badge>
                      )}
                    </HStack>
                  ))}
                </VStack>
              </Card.Body>
            </Card.Root>
          )}
        </VStack>

        {/* Timeline */}
        <VStack gap={4} align="stretch">
          <Card.Root>
            <Card.Header>
              <Text fontSize="lg" fontWeight="semibold">
                {copy["headings.events"]}
              </Text>
            </Card.Header>
            <Card.Body>
              <AlertTimeline events={alert?.events || []} loading={loading} />
            </Card.Body>
          </Card.Root>
        </VStack>
      </SimpleGrid>
    </VStack>
  )
}

export default AlertDetail